import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import Col from 'reactstrap/lib/Col';
import { Link } from 'react-scroll'; // eslint-disable-line no-restricted-syntax
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import {
  EditorialReviewModel,
  EditorialReviewEntities,
  buildReviewPath,
  buildRatedByPath,
  buildAuthorPath,
} from 'client/data/models/editorial-review';
import { buildWhatsNewPath, VehicleComparisonsEntities, VehicleDetailsModel } from 'client/data/models/mmy-details';
import { stripHtml } from 'client/utils/string-utils';
import { RATINGS_MAP } from 'site-modules/shared/constants/editorial-review';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { RELATED_ARTICLES_JUMP_LINK_LOCATION } from 'site-modules/shared/constants/editorial/editorial';
import { getRatingScale } from 'site-modules/shared/utils/editorial-helper';
import { separateFirstParagraph } from 'site-modules/shared/utils/string';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { FirstContentArticles } from 'site-modules/core-page/components/articles/first-content-articles/first-content-articles';
import { CoreScorecard } from 'site-modules/shared/components/editors-review/scorecard/core-scorecard';
import { TruncatedFragment } from 'site-modules/shared/components/truncated-fragment/truncated-fragment';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';
import {
  EditorialHighlightsListsUI,
  HIGHLIGHTS_NAME,
} from 'client/site-modules/shared/components/editorial-highlights-lists/editorial-highlights-lists';
import { MakeColumnNewsWidget } from 'site-modules/shared/components/column-news-widget/column-news-widget';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';
import { TrimsComparatorLink } from 'site-modules/shared/components/core-page/trims-comparator-link/trims-comparator-link';

import { ReviewSection } from './editorial-review-section';
import { USED_ITEMS, isUsedDataAvailable } from './utils';

import './editorial-review.scss';

const TRIMS_COMPARATOR_START_YEAR = 2005;

function renderRatingSection(ratings, sectionData) {
  const { title, value, children } = sectionData;
  return (
    ratings[value] && (
      <ReviewSection
        title={ratings[value].title || title}
        id={`${title}-section`}
        body={ratings[value].text}
        rating={ratings[value].rating}
      >
        {children.map(subSection => {
          const { title: subSectionTitle, value: subSectionValue } = subSection;
          return (
            ratings[subSectionValue] && (
              <ReviewSection
                id={`${subSectionValue}-${value}`}
                key={subSectionTitle}
                title={subSectionTitle}
                body={stripHtml(ratings[subSectionValue].text)}
                rating={ratings[subSectionValue].rating}
                titleClasses="size-16 fw-normal text-gray-darker"
              />
            )
          );
        })}
      </ReviewSection>
    )
  );
}

export function EditorialReviewBodyUI({
  editorialReview,
  whatsNewContent,
  vehicle,
  embeddedModules,
  isUsed,
  isMobile,
  hasRouteYearParam,
  headingText,
  forceShowScorecard,
  showHighlights,
  expandReview,
  isReordered,
  reviewAuthor,
  reviewRatedBy,
}) {
  if (!editorialReview || (isUsed && !isUsedDataAvailable(editorialReview))) {
    return null;
  }
  const {
    id: editorialReviewId,
    ratings = {},
    weRecommend,
    summary,
    summarySnippet,
    edmundsSays,
    trimFeaturesBodySnippet,
    body: editorialReviewBody,
    trimFeaturesBody,
    trimTested,
    whatsNew,
    powertrain,
    safety,
    introduction,
    review,
    reviewLink,
    firstContent,
  } = editorialReview;
  const {
    year,
    model: { name: modelName },
    make: { name: makeName, slug: makeSlug },
  } = vehicle;

  // All vehicle after 2017(included) has grouped review
  const hasGroupedReview = parseInt(year, 10) >= 2017;
  const articles = get(firstContent, 'articles');
  const media = get(firstContent, 'media');
  const hasRelatedArticles = firstContent && ((articles && articles.length > 0) || (media && media.length > 0));
  const scale = getRatingScale(editorialReview);
  const author = get(reviewAuthor, 'contentMetadata', {});
  const ratedBy = get(reviewRatedBy, 'contentMetadata', {});
  const buildPriceAdModule = get(embeddedModules, 'buildPriceAdModule');

  const hasHighlights =
    showHighlights &&
    (get(editorialReview, 'cons.length') ||
      get(editorialReview, 'pros.length') ||
      get(firstContent, 'bullets.value.length') ||
      get(whatsNewContent, 'length'));

  const editorialHighlights = !!hasHighlights && (
    <EditorialHighlightsListsUI
      editorialReview={editorialReview}
      firstContent={firstContent}
      whatsNewContent={whatsNewContent}
      className="mt-2 mb-1_5 mb-md-2"
      sectionsToRender={[HIGHLIGHTS_NAME.PROS, HIGHLIGHTS_NAME.CONS, HIGHLIGHTS_NAME.WHATS_NEW]}
      HeadingTag="h3"
    />
  );

  if (!reviewLink && !hasRelatedArticles) {
    return (
      !!editorialHighlights && (
        <Fragment>
          <h2 className="heading-3 fw-bold mb-1_5">{headingText || "Edmunds' Expert Review"}</h2>
          {editorialHighlights}
          {buildPriceAdModule}
        </Fragment>
      )
    );
  }

  const [modelsSnippet, modelsBody] = separateFirstParagraph(editorialReviewBody || '');
  const overallTitle = get(ratings, 'overall.title') || "Edmunds' Expert Rating";
  const overallRating = get(ratings, 'overall.rating') ? `${ratings.overall.rating} / ${scale}` : null;

  const hasWhatsNewSection = !hasGroupedReview || !get(whatsNewContent, 'length');
  const summaryTitle = isReordered || !hasGroupedReview ? '' : overallTitle;
  const modelsTrimsBody = trimFeaturesBody || modelsBody;

  const [firstSummary, restSummary] = separateFirstParagraph(summary || '');
  const rangeAndCostModule = get(embeddedModules, 'rangeAndCostModule');
  const coreSrpModule = get(embeddedModules, 'coreSrpModule');

  const overallRatingSection = (
    <Fragment>
      {hasWhatsNewSection && (
        <ReviewSection title="What’s new" id="whats-new-section" titleClasses="heading-4 mb-0_5" body={whatsNew} />
      )}

      {summary ? (
        <Fragment>
          {hasWhatsNewSection ? (
            <Fragment>
              <ReviewSection title="Vehicle overview" body={firstSummary} />
              {coreSrpModule}
              {rangeAndCostModule}
              <ReviewSection body={restSummary} />
            </Fragment>
          ) : (
            <Fragment>
              <ReviewSection
                title={summaryTitle}
                body={firstSummary}
                rating={overallRating}
                titleClasses="heading-4 mb-1"
              />
              {coreSrpModule}
              {rangeAndCostModule}
              <ReviewSection body={restSummary} />
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <ReviewSection
            title="Edmunds says"
            body={edmundsSays}
            rating={overallRating}
            titleClasses="heading-4 mb-0_5"
          />
          {coreSrpModule}
          {rangeAndCostModule}
          <ReviewSection title="Vehicle overview" body={introduction || review} />
        </Fragment>
      )}

      {!isReordered && (
        <Fragment>
          <ReviewSection
            title={`${hasRouteYearParam ? `${year} ` : ''}${makeName} ${modelName} models`}
            id="trim-levels-and-features-section"
            body={trimFeaturesBodySnippet || editorialReviewBody}
            titleClasses={classnames({ 'heading-4 mb-1': !isUsed })}
            afterBodyChildren={
              <MakeColumnNewsWidget makeSlug={makeSlug} makeName={makeName} modelName={modelName} className="my-1_5" />
            }
          />
          <ReviewSection body={trimFeaturesBody} id="adjusted-headers" />
        </Fragment>
      )}

      {isReordered && hasGroupedReview && (
        <ScrollElement id="subnav-rating-scorecard">
          <ReviewSection
            title={overallTitle}
            rating={overallRating}
            body={summarySnippet}
            titleClasses="heading-4 mb-1"
            allowNoBody
          />
        </ScrollElement>
      )}
      <ReviewSection title="Trim tested" id="trim-tested-section" body={trimTested} />
      {!hasGroupedReview && (
        <Fragment>
          <ReviewSection title="Performance & mpg" body={powertrain} />
          <ReviewSection title="Safety" body={safety} />
          {USED_ITEMS.map(
            ({ title, id, value, dataSet }) =>
              !!(dataSet && editorialReview[dataSet] && editorialReview[dataSet][value]) && (
                <ReviewSection
                  key={value}
                  title={title}
                  id={id}
                  body={editorialReview[dataSet][value].text}
                  rating={editorialReview[dataSet][value].rating}
                />
              )
          )}
        </Fragment>
      )}
    </Fragment>
  );

  // Show FL/FD content if has no editorial review
  if (!reviewLink) {
    return (
      <div className="px-0_5">
        {editorialHighlights}
        <FirstContentArticles articles={articles} isMobile={isMobile} suppressFirstMedia isUsed={isUsed} />
        <hr className="mt-1_5 mb-2" />
        {buildPriceAdModule}
      </div>
    );
  }

  const pricingAd = (
    <div
      className={classnames('editorial-pricing-card mt-2', {
        'is-wired': !isMobile,
        'is-mobile': isMobile,
      })}
    >
      <PricingAd mobile={isMobile} />
      {isMobile && <hr className="mt-1_5 mt-md-1 mb-1" />}
    </div>
  );

  const trimsComparatorLink = parseInt(year, 10) >= TRIMS_COMPARATOR_START_YEAR && (
    <TrimsComparatorLink className="mb-1_5" vehicle={vehicle} isMobile={isMobile} />
  );

  return (
    <div data-tracking-parent="edm-entry-editorial-review">
      <ScrollElement id={`scrollTop-${editorialReviewId}`} />
      {isUsed && isMobile && pricingAd}
      <div className="editorial-review-full-container">
        {hasGroupedReview && !isReordered && (
          <div className="mb-2">
            <ReviewSection
              title={`Which ${modelName} does Edmunds recommend?`}
              id="we-recommend-section"
              titleClasses="mb-1_5 heading-3"
              body={weRecommend}
            />
          </div>
        )}
        <h2 className="heading-3 fw-bold mb-1_5">{headingText || "Edmunds' Expert Review"}</h2>
        {author && <ArticleAuthor author={author} ratedBy={ratedBy} lazyLoadImage showBioTooltip />}
        {editorialHighlights}
        <TruncatedFragment
          content={overallRatingSection}
          minHeight={205}
          btnContainerClassName={null}
          btnClassName="mb-1_5"
          expandText="Read more"
          collapseText="Read less"
          ariaLabelHeading="about this model"
          resetParams={{
            offset: SCROLL_OFFSET,
            duration: SCROLL_DURATION,
            to: `scrollTop-${editorialReviewId}`,
          }}
          isTruncated={!expandReview}
        />
        {editorialReview.ratings && (isMobile || forceShowScorecard) && (
          <Col>
            <CoreScorecard ratings={editorialReview.ratings} scale={scale} className="my-1_5" />
          </Col>
        )}
        {hasGroupedReview && (
          <Fragment>
            {renderRatingSection(ratings, RATINGS_MAP.DRIVING)}
            {renderRatingSection(ratings, RATINGS_MAP.COMFORT)}
            {renderRatingSection(ratings, RATINGS_MAP.INTERIOR)}
            {renderRatingSection(ratings, RATINGS_MAP.UTILITY)}
            {renderRatingSection(ratings, RATINGS_MAP.TECHNOLOGY)}
          </Fragment>
        )}
        {isReordered && (
          <Fragment>
            {buildPriceAdModule}
            {hasGroupedReview && (
              <Fragment>
                <ReviewSection
                  title={`Which ${modelName} does Edmunds recommend?`}
                  id="we-recommend-section"
                  titleClasses="heading-4 mb-1"
                  body={weRecommend}
                />
                {trimsComparatorLink}
              </Fragment>
            )}
            <ReviewSection
              title={`${hasRouteYearParam ? `${year} ` : ''}${makeName} ${modelName} models`}
              id="trim-levels-and-features-section"
              body={trimFeaturesBodySnippet || modelsSnippet}
              titleClasses={classnames({ 'heading-4 mb-1': !isUsed })}
              afterBodyChildren={
                <Fragment>
                  {!hasGroupedReview && !modelsTrimsBody?.length && trimsComparatorLink}
                  <MakeColumnNewsWidget
                    makeSlug={makeSlug}
                    makeName={makeName}
                    modelName={modelName}
                    className="my-1_5"
                  />
                </Fragment>
              }
            >
              <ReviewSection body={modelsTrimsBody} id="adjusted-headers" />
            </ReviewSection>
            {!hasGroupedReview && !!modelsTrimsBody?.length && trimsComparatorLink}
          </Fragment>
        )}
        {hasRelatedArticles && (
          <div className="my-1 text-primary-darker">
            <span className="text-gray-darker">Jump to:</span>
            <Link
              className="related-articles-link size-16 ps-0_25"
              activeClass="text-info fw-medium"
              to={RELATED_ARTICLES_JUMP_LINK_LOCATION}
              offset={SCROLL_OFFSET}
              duration={SCROLL_DURATION}
              smooth
              isDynamic
            >
              Related {year} {modelName} articles
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

EditorialReviewBodyUI.propTypes = {
  editorialReview: EditorialReviewEntities.EditorialReview,
  reviewAuthor: EditorialReviewEntities.Author,
  reviewRatedBy: EditorialReviewEntities.Author,
  whatsNewContent: VehicleComparisonsEntities.WhatsNewContent,
  vehicle: PropTypes.shape({
    make: PropTypes.object,
    model: PropTypes.object,
    year: PropTypes.string,
    submodels: PropTypes.object,
    pubStates: PropTypes.object,
  }),
  embeddedModules: PropTypes.shape({
    rangeAndCostModule: PropTypes.node,
    coreSrpModule: PropTypes.node,
  }),
  isUsed: PropTypes.bool,
  forceShowScorecard: PropTypes.bool,
  isMobile: PropTypes.bool,
  hasRouteYearParam: PropTypes.bool,
  showHighlights: PropTypes.bool,
  headingText: PropTypes.string,
  expandReview: PropTypes.bool,
  isReordered: PropTypes.bool,
};

EditorialReviewBodyUI.defaultProps = {
  editorialReview: null,
  reviewAuthor: null,
  reviewRatedBy: null,
  firstContent: null,
  whatsNewContent: null,
  vehicle: null,
  embeddedModules: null,
  isUsed: false,
  forceShowScorecard: false,
  isMobile: true,
  hasRouteYearParam: false,
  showHighlights: false,
  headingText: null,
  expandReview: false,
  isReordered: false,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const EditorialReviewBody = connect(mapStateToProps)(
  connectToModel(EditorialReviewBodyUI, {
    reviewAuthor: bindToPath(({ params }) => buildAuthorPath(params), EditorialReviewModel),
    reviewRatedBy: bindToPath(({ params }) => buildRatedByPath(params), EditorialReviewModel),
    editorialReview: bindToPath(({ params }) => buildReviewPath(params), EditorialReviewModel),
    whatsNewContent: bindToPath(
      ({ params, showHighlights }) => showHighlights && buildWhatsNewPath(params),
      VehicleDetailsModel
    ),
  })
);
